import type { RideHistoryItem } from '@/api-services/hooks/dashboardTypes';
import { ApiResponse, PagedData } from '@/api-services/hooks/types';
import {
  useActiveRides,
  useOnDutyDriverList,
  useTodayGMV,
  useTodayRevenue,
  useTodayRideHistory,
  useTodayTrips,
} from '@/api-services/hooks/useDashboard';
import { useRiders } from '@/api-services/hooks/useRider';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Activity,
  AlertCircle,
  Car,
  CheckCircle,
  Clock,
  DollarSign,
  Loader2,
  MapPin,
  RefreshCw,
  TrendingUp,
  Users
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip
} from 'recharts';
import { useAllVehicleCategories } from '@/api-services/hooks/useVehicleCategory';
import { formatGlobalCurrency } from '@/api-services/hooks/useSettings';

// Helper function to extract data from API response
const extractData = <T,>(response: ApiResponse<T> | null): T | null => {
  if (!response) return null;
  if (response.data !== undefined && response.data !== null) return response.data as T;
  // API may return payload directly (number, array, or object) without wrapper
  if (Array.isArray(response)) return response as unknown as T;
  return null;
};

// Helper function to format currency (no decimals)
const formatCurrency = (amount: number | undefined): string => {
  if (amount === undefined || amount === null) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// Helper to format currency with decimals (e.g. for GMV)
const formatCurrencyWithDecimals = (amount: number | undefined): string => {
  if (amount === undefined || amount === null) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

// Helper function to format number
const formatNumber = (num: number | undefined): string => {
  if (num === undefined || num === null) return '0';
  return new Intl.NumberFormat('en-US').format(num);
};

const Dashboard = () => {
  const navigate = useNavigate();
  // State for real-time driver count updates
  const [lastRefreshTime, setLastRefreshTime] = useState<Date | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('ALL');

  // Fetch dashboard data
  const { data: todayTripsResponse, loading: tripsLoading, error: tripsError, refetch: refetchTrips } = useTodayTrips({
    onError: (error) => {
      console.error('Failed to load today trips:', error);
    },
  });
  const { data: todayRevenueResponse, loading: revenueLoading, error: revenueError, refetch: refetchRevenue } = useTodayRevenue({
    onError: (error) => {
      console.error('Failed to load today revenue:', error);
    },
  });
  const { data: todayGMVResponse, loading: gmvLoading, error: gmvError, refetch: refetchGMV } = useTodayGMV({
    onError: (error) => {
      console.error('Failed to load today GMV:', error);
    },
  });
  const { data: onDutyDriversResponse, loading: driversLoading, error: driversError, refetch: refetchDrivers } = useOnDutyDriverList({
    onError: (error) => {
      console.error('Failed to load on-duty drivers:', error);
    },
  });
  const { data: activeRidesResponse, loading: activeRidesLoading, error: activeRidesError, refetch: refetchActiveRides } = useActiveRides({
    onError: (error) => {
      console.error('Failed to load active rides:', error);
    },
  });
  
  // Fetch riders list to count active riders
  const { data: ridersData, isLoading: ridersLoading, error: ridersError, refetch: refetchRiders } = useRiders(
    { page: 0, size: 1000 }, // Fetch a large number to get all riders
    {
      onError: (error) => {
        console.error('Failed to load riders:', error);
      },
    }
  );
  
  const { data: rideHistoryResponse, loading: rideHistoryLoading, error: rideHistoryError, refetch: refetchRideHistory } = useTodayRideHistory({
    onError: (error) => {
      // Silently ignore 404 and 500 errors - endpoint may not be implemented or may have issues
      if (!error.message.includes('404') && !error.message.includes('500')) {
        console.error('Failed to load ride history:', error);
      }
    },
  });

  // Refetch all dashboard data (used by interval and tab visibility)
  const refetchAll = useCallback(() => {
    Promise.all([
      refetchTrips?.(),
      refetchRevenue?.(),
      refetchGMV?.(),
      refetchDrivers?.(),
      refetchActiveRides?.(),
      refetchRiders?.(),
      refetchRideHistory?.(),
    ]).finally(() => setLastRefreshTime(new Date()));
  }, [refetchTrips, refetchRevenue, refetchGMV, refetchDrivers, refetchActiveRides, refetchRiders, refetchRideHistory]);

  // Auto-refresh all dashboard data every 30 seconds
  useEffect(() => {
    const interval = setInterval(refetchAll, 30000);
    return () => clearInterval(interval);
  }, [refetchAll]);

  // Refresh when user switches back to this tab (browser tab visibility)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refetchAll();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [refetchAll]);

  // Manual refresh handler
  const handleManualRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        refetchTrips?.(),
        refetchRevenue?.(),
        refetchGMV?.(),
        refetchDrivers?.(),
        refetchActiveRides?.(),
        refetchRiders?.(),
        refetchRideHistory?.()
      ]);
      setLastRefreshTime(new Date());
    } finally {
      setIsRefreshing(false);
    }
  };

  // Extract data from responses
  const tripsData = extractData(todayTripsResponse);
  const revenueData = extractData(todayRevenueResponse);
  const gmvData = extractData(todayGMVResponse);
  const onDutyDrivers = extractData(onDutyDriversResponse) || [];
  const activeRides = extractData(activeRidesResponse) || [];

  // Calculate active riders count from riders list
  const activeRidersCount = useMemo(() => {
    if (!ridersData?.content) return 0;
    return ridersData.content.filter(rider => rider.isActive === true).length;
  }, [ridersData]);

  // Extract ride history data
  const rideHistoryData = extractData(rideHistoryResponse) as PagedData<RideHistoryItem> | null;
  const rideHistoryItems = rideHistoryData?.content || [];

  // Calculate revenue from trips data or ride history
  // Handle both array format (individual rides) and object format (summary)
  const calculatedRevenueFromTrips = useMemo(() => {
    if (Array.isArray(tripsData)) {
      // tripsData is an array of ride objects - sum up actualFare from completed rides
      return tripsData.reduce((sum: number, ride: any) => {
        const fare = ride.actualFare || ride.fare || ride.totalFare || 0;
        if (ride.status === 'COMPLETED') {
          return sum + (typeof fare === 'number' ? fare : 0);
        }
        return sum;
      }, 0);
    }
    // tripsData is a summary object
    return tripsData?.totalRevenue || tripsData?.totalFare || 0;
  }, [tripsData]);

 const { data: vehicleCategoriesData } = useAllVehicleCategories() as { data: { vehicleCategoryId: number; vehicleCategoryName: string }[] | undefined };

  // Build a lookup map for vehicle category ID -> name
  const vehicleCategoryMap = useMemo(() => {
    const map: Record<number, string> = {};
    if (vehicleCategoriesData) {
      vehicleCategoriesData.forEach((cat) => {
        map[cat.vehicleCategoryId] = cat.vehicleCategoryName;
      });
    }
    return map;
  }, [vehicleCategoriesData]);

  // Calculate revenue from ride history (sum of all fares)
  const calculatedRevenueFromRides = useMemo(() => {
    if (!Array.isArray(rideHistoryItems)) return 0;

    return rideHistoryItems.reduce((sum: number, ride: RideHistoryItem) => {
      // Try different possible field names for fare
      const fare = ride.fare || ride.totalFare || ride.amount || ride.revenue || 0;
      // Only count completed rides
      if (ride.status === 'COMPLETED' || ride.status === 'completed' || !ride.status) {
        return sum + (typeof fare === 'number' ? fare : 0);
      }
      return sum;
    }, 0);
  }, [rideHistoryItems]);

  // Use revenue from API if available, otherwise calculate from trips or ride history
  // revenueData can be a plain number or an object with totalRevenue/todayRevenue properties
  const totalRevenueRaw = (
    (typeof revenueData === 'number' ? revenueData : null) ??
    (revenueData?.totalRevenue) ??
    (revenueData?.todayRevenue) ??
    (revenueData?.revenue) ??
    calculatedRevenueFromTrips
  ) ||
    calculatedRevenueFromRides ||
    0;
  const totalRevenue = parseFloat(String(totalRevenueRaw)) || 0;

  const totalGMV = (typeof gmvData === 'number' ? gmvData : null) ||
    (gmvData?.totalGMV) ||
    (gmvData?.todayGMV) ||
    0;

  // Calculate total trips - handle both array and summary object formats
  const activeDriversCount = onDutyDrivers.length;
  const activeRidesCount = activeRides.filter(
    (ride: any) => ride.status !== 'REQUESTED'
    ).length;

  // Prepare chart data (using trips data for ride status)
  const rideStatusData = useMemo(() => {
    let completed = 0;
    let active = 0;
    let cancelled = 0;

    if (Array.isArray(tripsData)) {
      // Count statuses from ride array
      completed = tripsData.filter((r: any) => r.status === 'COMPLETED').length;
      active = tripsData.filter((r: any) => r.status === 'REQUESTED' || r.status === 'ACCEPTED' || r.status === 'ARRIVED' || r.status === 'STARTED').length;
      cancelled = tripsData.filter((r: any) => r.status === 'CANCELLED').length;
    } else {
      // Use summary object values
      completed = tripsData?.completedTrips || 0;
      active = tripsData?.activeTrips || activeRidesCount;
      cancelled = tripsData?.cancelledTrips || 0;
    }

    return [
      { name: 'Completed', value: completed, color: 'hsl(var(--success))' },
      { name: 'Active', value: active, color: 'hsl(var(--primary))' },
      { name: 'Cancelled', value: cancelled, color: 'hsl(var(--destructive))' },
    ].filter(item => item.value > 0);
  }, [tripsData, activeRidesCount]);

  // Loading state - include ride history loading for revenue calculation
  const isLoading = tripsLoading || revenueLoading || gmvLoading || driversLoading || activeRidesLoading || ridersLoading || rideHistoryLoading;
  const hasError = tripsError || revenueError || gmvError || driversError || activeRidesError || ridersError;
  // Note: rideHistoryError is excluded from hasError to prevent 404 from breaking the dashboard
  // The /reports/ride-history endpoint may not exist on the backend
  const filteredRides = useMemo(() => {
  return activeRides.filter((ride: any) => {
    if (statusFilter === 'ALL') return true;

    if (statusFilter === 'ACCEPTED') {
      return ride.status === 'ACCEPTED' || ride.status === 'ARRIVED';
    }

    return ride.status === statusFilter;
  });
}, [activeRides, statusFilter]);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Dashboard</h1>
          <p className="text-muted-foreground mt-1">Welcome back! Here's what's happening with your fleet.</p>
          {lastRefreshTime && (
            <p className="text-xs text-muted-foreground mt-1">
              Last updated: {lastRefreshTime.toLocaleTimeString()}
            </p>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <Badge variant="secondary" className="animate-glow">
            <Activity className="w-3 h-3 mr-1" />
            Live
          </Badge>
          <Button
            variant="outline"
            size="sm"
            onClick={handleManualRefresh}
            disabled={isRefreshing}
            className="gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>
      </div>

      {/* Error State */}
      {hasError && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              <p>Error loading dashboard data. Please try again later.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
        <Card className="hover-lift interactive border-metrics-border bg-metrics-bg/50">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Today's Revenue</CardTitle>
          </CardHeader>
          <CardContent>
            {(revenueLoading || rideHistoryLoading) ? (
              <div className="flex items-center space-x-2">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Loading...</span>
              </div>
            ) : (
              <>
                <div className="text-2xl font-bold text-foreground">{formatGlobalCurrency(totalRevenue)}</div>
                <p className="text-xs text-muted-foreground flex items-center mt-1">
                  <TrendingUp className="w-3 h-3 mr-1" />
                  {calculatedRevenueFromRides > 0
                    ? `Calculated from ${rideHistoryItems.length} rides`
                    : calculatedRevenueFromTrips > 0
                      ? 'From trips data'
                      : 'Today\'s total revenue'}
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <Card className="hover-lift interactive border-metrics-border bg-metrics-bg/50">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">On-Duty Drivers</CardTitle>
          </CardHeader>
          <CardContent>
            {driversLoading ? (
              <div className="flex items-center space-x-2">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Loading...</span>
              </div>
            ) : (
              <>
                <div className="text-2xl font-bold text-foreground">{formatNumber(activeDriversCount)}</div>
                <p className="text-xs text-success flex items-center mt-1">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Currently on duty
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <Card className="hover-lift interactive border-metrics-border bg-metrics-bg/50">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Active Riders</CardTitle>
            <Users className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            {ridersLoading ? (
              <div className="flex items-center space-x-2">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Loading...</span>
              </div>
            ) : (
              <>
                <div className="text-2xl font-bold text-foreground">{formatNumber(activeRidersCount)}</div>
                <p className="text-xs text-primary flex items-center mt-1">
                  <Activity className="w-3 h-3 mr-1" />
                  Currently active
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <Card className="hover-lift interactive border-metrics-border bg-metrics-bg/50">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Today's Trips</CardTitle>
          </CardHeader>
          <CardContent>
            {tripsLoading ? (
              <div className="flex items-center space-x-2">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Loading...</span>
              </div>
            ) : (
              <>
                <div className="text-2xl font-bold text-foreground">
                  {Array.isArray(tripsData) ? tripsData.filter((r: any) => (r.status || '').toUpperCase() === 'COMPLETED').length : (tripsData?.completedTrips ?? 0)}
                </div>
                <p className="text-xs text-muted-foreground flex items-center mt-1">
                  <TrendingUp className="w-3 h-3 mr-1" />
                  Completed trips today
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <Card className="hover-lift interactive border-metrics-border bg-metrics-bg/50">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Active Rides</CardTitle>
            <Activity className="h-4 w-4 text-warning" />
          </CardHeader>
          <CardContent>
            {activeRidesLoading ? (
              <div className="flex items-center space-x-2">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Loading...</span>
              </div>
            ) : (
              <>
                <div className="text-2xl font-bold text-foreground">{formatNumber(activeRidesCount)}</div>
                <p className="text-xs text-warning flex items-center mt-1">
                  <Clock className="w-3 h-3 mr-1" />
                  Currently in progress
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* GMV Chart */}
        <Card className="hover-lift">
          <CardHeader>
            <CardTitle className="text-foreground">Today's GMV</CardTitle>
          </CardHeader>
          <CardContent>
            {gmvLoading ? (
              <div className="h-80 flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="h-80 chart-enter">
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className="text-4xl font-bold text-foreground mb-2">
                      {formatGlobalCurrency(totalGMV)}
                    </div>
                    <p className="text-sm text-muted-foreground">Gross Merchandise Value</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Ride Status Chart */}
        <Card className="hover-lift">
          <CardHeader>
            <CardTitle className="text-foreground">Ride Status Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            {tripsLoading ? (
              <div className="h-80 flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : rideStatusData.length === 0 ? (
              <div className="h-80 flex items-center justify-center">
                <p className="text-muted-foreground">No ride data available</p>
              </div>
            ) : (
              <div className="h-80 chart-enter">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={rideStatusData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(2)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {rideStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px',
                        color: 'hsl(var(--foreground))'
                      }}
                      cursor={false}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Active Rides List */}
      {activeRides.length > 0 && (
        <Card className="hover-lift">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-foreground">Active Rides</CardTitle>
              <div className="flex items-center gap-1.5">
                {['ALL', 'REQUESTED', 'ACCEPTED', 'STARTED'].map((status) => (
                  <Button
                    key={status}
                    variant={statusFilter === status ? 'default' : 'outline'}
                    size="sm"
                    className={`text-xs h-7 ${
                      statusFilter === status
                        ? status === 'REQUESTED' ? 'bg-warning hover:bg-warning/90' :
                          (status === 'ACCEPTED' ||  status === 'ARRIVED') ? 'bg-primary hover:bg-primary/90' :
                          status === 'STARTED' ? 'bg-success hover:bg-success/90' :
                          'bg-primary hover:bg-primary/90'
                        : ''
                    }`}
                    onClick={() => setStatusFilter(status)}
                  >
                    {status.charAt(0) + status.slice(1).toLowerCase()}
                  </Button>
                ))}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="max-h-[500px] overflow-y-auto space-y-3 pr-2">
              {[...filteredRides]
                .sort((a: any, b: any) => {
                  const idA = a.bookingId ?? '';
                  const idB = b.bookingId ?? '';
                  const numA = Number(idA);
                  const numB = Number(idB);
                  if (!Number.isNaN(numA) && !Number.isNaN(numB)) return numB - numA; // numeric: latest (highest) first
                  return -String(idA).localeCompare(String(idB), undefined, { numeric: true }); // string: latest first
                })
.filter((ride: any) => {
  if (statusFilter === 'ALL') return true;

  if (statusFilter === 'ACCEPTED') {
    return ride.status === 'ACCEPTED' || ride.status === 'ARRIVED';
  }

  return ride.status === statusFilter;
})                .map((ride: any, index: number) => {
                  const statusColors: Record<string, string> = {
                    REQUESTED: 'bg-warning',
                    ACCEPTED: 'bg-primary',
                    STARTED: 'bg-success',
                  };

                  const formatAddress = (address: string) => {
                    if (!address) return 'Unknown';
                    const parts = address.split(',');
                    if (parts.length <= 2) return address;
                    return parts.slice(0, 2).join(',') + '...';
                  };

                  const rideDateTime = ride.requestedTime || ride.createdAt || ride.bookingDate;
                  const formattedDateTime = rideDateTime
                    ? new Date(rideDateTime).toLocaleString('en-US', {
                        month: 'short', day: 'numeric', year: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                      })
                    : null;
                  const riderName = ride.rider?.riderName || ride.riderId;
                  const driverName = ride.driver?.driverName || ride.driverId;
                  const vehicleName = ride.vehicleCategoryId || ride.vehicleType || ride.vehicle?.vehicleType || ride.vehicle?.vehicleCategoryName || '';

                  return (
                    <div
                      key={ride.bookingId || index}
                      className="p-4 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors border border-border/50"
                    >
                      {/* Header: Booking ID + Status + Fare */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <span className="font-semibold text-sm">#{ride.bookingId || 'N/A'}</span>
                          <Badge
                            variant="outline"
                            className={`text-xs ${statusColors[ride.status] || 'bg-muted'} text-white border-0`}
                          >
                            {ride.status || 'UNKNOWN'}
                          </Badge>
                        </div>
                        {ride.estimatedFare && (
                          <span className="text-sm font-medium">
                            {formatGlobalCurrency(ride.estimatedFare)}
                            {ride.actualFare && ride.actualFare !== ride.estimatedFare && (
                              <span className="text-muted-foreground text-xs ml-1">
                                ({formatGlobalCurrency(ride.actualFare)})
                              </span>
                            )}
                          </span>
                        )}
                      </div>

                      {/* Ride Date & Time */}
                      {formattedDateTime && (
                        <div className="flex items-center text-xs text-muted-foreground mb-3">
                          <Clock className="w-3 h-3 mr-1" />
                          {formattedDateTime}
                        </div>
                      )}


                      {/* Rider & Driver Info */}
                      <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground mb-3">
                        {riderName && (
                          <span className="flex items-center">
                            <Users className="w-3 h-3 mr-1" />
                            {riderName}
                          </span>
                        )}
                        {driverName && (
                          <span className="flex items-center">
                            <Car className="w-3 h-3 mr-1" />
                            {driverName}
                          </span>
                        )}
                        {vehicleName && (
                          <span className="flex items-center">
                            <Car className="w-3 h-3 mr-1" />
                            {vehicleCategoryMap[vehicleName] || vehicleName}
                          </span>
                        )}
                        {ride.rideDistance && (
                          <span className="flex items-center">
                            <MapPin className="w-3 h-3 mr-1" />
                            {ride.rideDistance.toFixed(2)} km
                          </span>
                        )}
                        {ride.paymentType && (
                          <Badge variant="outline" className="text-xs h-4">
                            {ride.paymentType}
                          </Badge>
                        )}
                      </div>

                      {/* Pickup & Dropoff */}
                      <div className="space-y-1.5 text-sm">
                        <div className="flex items-start space-x-2">
                          <div className="w-2 h-2 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <span className="text-muted-foreground text-xs">Source (From):</span>
                            <p className="text-foreground truncate" title={ride.riderAddress}>
                              {formatAddress(ride.riderAddress)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start space-x-2">
                          <div className="w-2 h-2 rounded-full bg-success mt-1.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <span className="text-muted-foreground text-xs">Destination (To):</span>
                            <p className="text-foreground truncate" title={ride.dropAddress}>
                              {formatAddress(ride.dropAddress)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              {filteredRides.length === 0 && (
                <div className="text-center py-8 text-muted-foreground text-sm">
                  No rides found for this filter
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Dashboard;